/** Folders as the first cards of the cards view: each a link to its page, with how many items it holds. */
import type { Crumb, FolderRow, SpaceOut } from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import { folderHref } from "../../lib/folders.ts";
import { location } from "../../lib/router.ts";
import { Link } from "../../ui/Button.tsx";
import { FolderCommentCount } from "../../ui/counts.tsx";
import { FolderIcon } from "../../ui/icons.tsx";
import { LINK } from "../../ui/styles.ts";
import { VisibilityMark } from "../item/visibility.tsx";
import { PeekButton } from "../items/icons.tsx";
import { Breadcrumb } from "./breadcrumb.tsx";
import { DROP_OVER, dragIds, useDropInto } from "./drop.ts";

/** A folder's card: its name (to its page), who sees it, its comments, what's in it; `path`, where it is. */
function FolderCard(p: {
  space: SpaceOut;
  folder: FolderRow;
  path?: readonly Crumb[] | undefined;
  /** Dragged into another folder (a board's column, a folder's card or row), and things dropped on it go into it. */
  draggable?: boolean;
}) {
  const { folder } = p;
  const drop = useDropInto(p.space, folder.id, folder.name, !p.draggable);
  const facts = [
    t("folders.itemsInside", { count: folder.count }),
    folder.folders > 0 ? t("folders.count.folders", { count: folder.folders }) : null,
  ];
  return (
    <article
      class={`flex flex-col gap-1 rounded-lg border border-zinc-200 bg-zinc-50 p-4 ${p.draggable ? "cursor-grab active:cursor-grabbing" : ""} ${drop.over ? DROP_OVER : ""}`}
      draggable={p.draggable}
      onDragStart={p.draggable ? (e) => dragIds(e, [folder.id], folder.parent ?? null) : undefined}
      {...drop.props}
    >
      <span class="flex min-w-0 items-center gap-2">
        <PeekButton
          space={p.space}
          id={folder.id}
          name={folder.name}
          icon={<FolderIcon size={18} class="text-zinc-500" />}
        />
        <Link
          href={folderHref(p.space, folder.id, location.value.searchParams)}
          draggable={false}
          class={`${LINK} min-w-0 [overflow-wrap:anywhere]`}
        >
          {folder.name}
        </Link>
        <VisibilityMark item={folder} members={undefined} />
        <FolderCommentCount space={p.space} id={folder.id} />
      </span>
      {p.path && p.path.length > 1 ? <Breadcrumb space={p.space} path={p.path.slice(0, -1)} small={true} /> : null}
      <p class="text-xs text-zinc-500">{facts.filter(Boolean).join(" · ")}</p>
    </article>
  );
}

/** The folders a cards view starts with, in a grid of their own above the items' (or a board's, a column before its). */
export function FolderCards(p: {
  space: SpaceOut;
  folders: readonly FolderRow[];
  pathOf?: ((id: string) => readonly Crumb[]) | undefined;
  /** One under the other: a board's first column (no frame of its own: not a column to drop on), or the top of a
   *  folder's column (`stack`); all of them. */
  layout?: "column" | "stack";
  /** Each can be dragged onto a board's column (moved there). */
  draggable?: boolean;
}) {
  if (p.folders.length === 0) {
    return null;
  }
  return (
    <div
      class={
        p.layout === "column"
          ? "flex w-64 shrink-0 flex-col gap-2 pt-9"
          : p.layout === "stack"
            ? "flex flex-col gap-2"
            : "mb-3 grid grid-cols-[repeat(auto-fill,minmax(18rem,1fr))] items-start gap-3"
      }
    >
      {p.folders.map((f) => (
        <FolderCard key={f.id} space={p.space} folder={f} path={p.pathOf?.(f.id)} draggable={p.draggable ?? false} />
      ))}
    </div>
  );
}
